import { useState } from 'react';
import type { FC, MouseEvent } from 'react';
import { Action } from '@interstate/components/Action';
import { CheckBox } from '@interstate/components/CheckBox';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Typography } from '@interstate/components/Typography';
import { ConsentDisclosureModal } from './ConsentDisclosureModal';
import { ConsentLabel, ConsentSection } from './creditConsent.styles';
import {
    CONSENT_AGREEMENT_CONJUNCTION,
    CONSENT_AGREEMENT_PREFIX,
    CREDIT_DISCLOSURE_HEADER,
    PRIVACY_NOTICE_BODY,
    PRIVACY_NOTICE_HEADER,
    TERMS_AND_CONDITIONS_BODY,
    TERMS_AND_CONDITIONS_HEADER,
    buildConsentAcknowledgement
} from './creditConsent.constants';

export type CreditConsentProps = {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Namespaces every id and data-testid so buyer and co-buyer instances stay distinct. */
    idPrefix: string;
    /** The host's confirm button, interpolated into the acknowledgement copy. SCI uses "Save", MV uses "Apply". */
    actionLabel?: string;
    /** Fired when a disclosure link is opened, so hosts can emit their own analytics. */
    onDisclosureOpen?: (disclosure: 'terms' | 'privacy') => void;
};

/**
 * Credit-consent box (F311457) — captures the customer's authorization to pull a credit bureau
 * report and submit a credit application, which is what gates sending their PII downstream.
 *
 * Deliberately controlled and store-free so both customer card variants can render it: the activity
 * card binds it to the validation engine, the legacy card to offer-redux.
 */
export const CreditConsent: FC<CreditConsentProps> = ({
    checked,
    onChange,
    disabled = false,
    idPrefix,
    actionLabel = 'Save',
    onDisclosureOpen
}) => {
    const [openDisclosure, setOpenDisclosure] = useState<'terms' | 'privacy' | null>(null);

    // The links sit inside the checkbox's <label>, so a bare click would toggle consent as well as
    // open the modal. Suppress that — opening a disclosure must never change the user's answer.
    const openFromLabel = (disclosure: 'terms' | 'privacy') => (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setOpenDisclosure(disclosure);
        onDisclosureOpen?.(disclosure);
    };

    const handleChange = (event: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        onChange(event.target.value.checked);
    };

    return (
        <ConsentSection data-testid={`${idPrefix}-credit-consent`}>
            <Typography variant="h5" sx={{ marginBottom: '1rem' }} data-testid={`${idPrefix}-credit-consent-header`}>
                {CREDIT_DISCLOSURE_HEADER}
            </Typography>

            <CheckBox
                id={`${idPrefix}-credit-consent-checkbox`}
                data-testid={`${idPrefix}-credit-consent-checkbox`}
                checked={checked}
                value=""
                disabled={disabled}
                onChange={handleChange}
                sx={{ backgroundColor: 'transparent' }}
                label={
                    <ConsentLabel>
                        <Typography variant="body-md" tag="span">
                            {CONSENT_AGREEMENT_PREFIX}
                            <Action
                                size="md"
                                onClick={openFromLabel('terms')}
                                data-testid={`${idPrefix}-credit-consent-terms-link`}
                            >
                                {TERMS_AND_CONDITIONS_HEADER}
                            </Action>
                            {CONSENT_AGREEMENT_CONJUNCTION}
                            <Action
                                size="md"
                                onClick={openFromLabel('privacy')}
                                data-testid={`${idPrefix}-credit-consent-privacy-link`}
                            >
                                {PRIVACY_NOTICE_HEADER}
                            </Action>
                            .
                        </Typography>
                        <Typography variant="body-md" tag="span" data-testid={`${idPrefix}-credit-consent-acknowledgement`}>
                            {buildConsentAcknowledgement(actionLabel)}
                        </Typography>
                    </ConsentLabel>
                }
            />

            <ConsentDisclosureModal
                show={openDisclosure === 'terms'}
                id={`${idPrefix}-credit-consent-terms-modal`}
                header={TERMS_AND_CONDITIONS_HEADER}
                paragraphs={TERMS_AND_CONDITIONS_BODY}
                onClose={() => setOpenDisclosure(null)}
                size="large"
            />
            <ConsentDisclosureModal
                show={openDisclosure === 'privacy'}
                id={`${idPrefix}-credit-consent-privacy-modal`}
                header={PRIVACY_NOTICE_HEADER}
                paragraphs={PRIVACY_NOTICE_BODY}
                onClose={() => setOpenDisclosure(null)}
            />
        </ConsentSection>
    );
};
